import { Button, Divider, NumberInput, Select, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChartDots3, IconDeviceFloppy, IconPlayerPlay, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { clientsApi } from "#/api/clientsApi";
import { showErrorNotification, showSuccessNotification } from "#/components/notifications";
import FieldDescription from "#/components/ui/FieldDescription";
import { useClient } from "#/hook/useClient";
import type { Client } from "#/types/client";
import { type PrvConfig, type PrvRun, PrvSchedule } from "#/types/prv";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";
import PrvResetModal from "./PrvResetModal";
import { nextScheduledRun } from "./prvUtils";

interface Props {
  clientId: number;
  client: Client;
  /** Latest run of any status, for the "last run" line. */
  latestRun: PrvRun | null;
  /** A completed run exists, so patterns can be shown. */
  canViewPatterns: boolean;
  runInProgress: boolean;
  onRunNow: () => void;
  onViewPatterns: () => void;
  onSaved: () => void;
}

const DEFAULTS: PrvConfig = {
  prvSchedule: PrvSchedule.DAILY,
  prvMinDwellMinutes: 120,
};

function configOf(client: Client): PrvConfig {
  return {
    prvSchedule: client.prvSchedule ?? DEFAULTS.prvSchedule,
    prvMinDwellMinutes: client.prvMinDwellMinutes ?? DEFAULTS.prvMinDwellMinutes,
  };
}

/** Right column of the PRV tab: the client's engine settings and the actions. */
const PrvConfigPanel = ({
  clientId,
  client,
  latestRun,
  canViewPatterns,
  runInProgress,
  onRunNow,
  onViewPatterns,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const { client: viewerClient } = useClient();
  const [resetOpened, { open: openReset, close: closeReset }] = useDisclosure(false);

  const form = useForm<PrvConfig>({
    initialValues: configOf(client),
    validate: {
      prvMinDwellMinutes: (value) =>
        Number.isInteger(value) && value >= 0 ? null : t("clients.prv.config.minDwellInvalid"),
    },
  });

  // Re-sync when the client record is refetched (e.g. after a save).
  useEffect(() => {
    form.setValues(configOf(client));
    form.resetDirty(configOf(client));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.prvSchedule, client.prvMinDwellMinutes]);

  const save = useMutation({
    mutationFn: (values: PrvConfig) => clientsApi.update(clientId, values),
    onSuccess: () => {
      showSuccessNotification({ message: t("clients.prv.config.saved"), color: "green.5" });
      onSaved();
    },
    onError: (error: Error) => {
      logger.error("PrvConfigPanel save error:", error);
      showErrorNotification({ message: error.message ?? t("common.failedToProcess") });
    },
  });

  const scheduleOptions = Object.values(PrvSchedule).map((value) => ({
    value,
    label: t(`clients.prv.schedule.${value}`),
  }));

  const nextRun = nextScheduledRun(form.values.prvSchedule, latestRun?.startedAt ?? null);
  const hasData = latestRun !== null;

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-4"
        onSubmit={form.onSubmit((values) => save.mutate(values))}
      >
        <Select
          label={t("clients.prv.config.schedule")}
          description={t("clients.prv.config.scheduleDescription")}
          data={scheduleOptions}
          allowDeselect={false}
          {...form.getInputProps("prvSchedule")}
        />
        {/* The radius is the client's Geofence Radius (Operations section), shared with the sub-client tabs and alerts. */}
        <span className="text-xs text-gray-600">
          {t("clients.prv.config.radiusFromGeofence", { radius: client.geofenceRadius ?? "--" })}
        </span>
        <NumberInput
          label={t("clients.prv.config.minDwell")}
          description={
            <FieldDescription
              lead={t("clients.prv.config.minDwellLead")}
              points={t("clients.prv.config.minDwellPoints", { returnObjects: true }) as string[]}
            />
          }
          min={0}
          step={15}
          allowDecimal={false}
          {...form.getInputProps("prvMinDwellMinutes")}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            leftSection={<IconDeviceFloppy size={18} />}
            disabled={!form.isDirty()}
            loading={save.isPending}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>

      <Divider label={t("clients.prv.actions.title")} labelPosition="left" />

      <div className="flex flex-col gap-3">
        <Button
          fullWidth
          leftSection={<IconPlayerPlay size={18} />}
          onClick={onRunNow}
          loading={runInProgress}
          loaderProps={{ type: "dots" }}
        >
          {runInProgress ? t("clients.prv.actions.running") : t("clients.prv.actions.runNow")}
        </Button>
        <Tooltip
          label={t("clients.prv.actions.viewPatternsHint")}
          disabled={canViewPatterns}
          withArrow
        >
          <Button
            fullWidth
            variant="outline"
            leftSection={<IconChartDots3 size={18} />}
            onClick={onViewPatterns}
            disabled={!canViewPatterns}
          >
            {t("clients.prv.actions.viewPatterns")}
          </Button>
        </Tooltip>

        <div className="flex flex-col gap-0.5 text-xs text-gray-500">
          <span>
            {t("clients.prv.actions.lastRun")}:{" "}
            {latestRun
              ? `${formatDateTime(latestRun.startedAt, viewerClient)} · ${t(`clients.prv.status.${latestRun.status}`)}`
              : t("clients.prv.actions.neverRun")}
          </span>
          <span>
            {form.values.prvSchedule === PrvSchedule.PAUSED
              ? t("clients.prv.actions.pausedHint")
              : `${t("clients.prv.actions.nextRun")}: ${nextRun ? formatDateTime(nextRun, viewerClient) : "--"}`}
          </span>
        </div>
      </div>

      <Divider label={t("clients.prv.reset.dividerLabel")} labelPosition="left" color="red.3" />

      <div className="flex flex-col gap-2">
        <Tooltip
          label={
            runInProgress
              ? t("clients.prv.reset.blockedRunning")
              : t("clients.prv.reset.nothingToReset")
          }
          disabled={!runInProgress && hasData}
          withArrow
        >
          <Button
            fullWidth
            color="red"
            variant="outline"
            leftSection={<IconTrash size={18} />}
            onClick={openReset}
            disabled={runInProgress || !hasData}
          >
            {t("clients.prv.reset.button")}
          </Button>
        </Tooltip>
        <span className="text-xs text-gray-500">{t("clients.prv.reset.hint")}</span>
      </div>

      <PrvResetModal
        opened={resetOpened}
        onClose={closeReset}
        clientId={clientId}
        clientName={client.name}
      />
    </div>
  );
};

export default PrvConfigPanel;
